import { ContractApiResponse, ParticipantResponse } from '../../types/api-responses';
import { ContractGuaranteeItem } from '../../types/operations';
import { loadBusinessPartnerFromParticipants } from '../helpers/operationHelpers';
import { BusinessPartnerData } from '../../components/form/BusinessPartnerManager';

// ============================================
// METADATA HELPERS
// ============================================

export interface ContractMetadata {
    apiVersion?: string;
    amendmentReason?: string;
    amendmentNumber?: number;
    billingStatus: { billed?: boolean; billing_info?: string };
}

export const extractMetadata = (data: ContractApiResponse): ContractMetadata => ({
    apiVersion: data.apiName && data.apiVersion ? `${data.apiName} - ${data.apiVersion}` : undefined,
    amendmentReason: data.amendment_reason || undefined,
    amendmentNumber: data.amendment_number || undefined,
    billingStatus: { billed: data.billed, billing_info: data.billing_info || undefined },
});

// ============================================
// PARTICIPANTS HELPERS
// ============================================

export interface ParticipantInfo {
    uuid: string;
    name: string;
    document: string;
    id: number | null;
    person_id: string;
}

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

const mapParticipantToInfo = (participant: ParticipantResponse): ParticipantInfo => ({
    uuid: participant.person_id || EMPTY_UUID,
    name: participant.name || '',
    document: participant.document || '',
    id: participant.id ? Number(participant.id) : null,
    person_id: participant.person_id || EMPTY_UUID,
});

export const extractAssignor = (data: ContractApiResponse): ParticipantInfo | null => {
    const assignor = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    return assignor ? mapParticipantToInfo(assignor) : null;
};

export const extractRecipient = (data: ContractApiResponse): ParticipantInfo | null => {
    const recipient = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');
    return recipient ? mapParticipantToInfo(recipient) : null;
};

export const extractBusinessPartner = (data: ContractApiResponse): BusinessPartnerData | null =>
    loadBusinessPartnerFromParticipants(data.participants);

// ============================================
// FILES HELPERS
// ============================================

export interface ExistingFiles {
    contractFile: string;
    artifactFiles: string[];
}

export const extractFilesFromSchema = (schema: any): ExistingFiles | null => {
    if (!schema.files) return null;
    return {
        contractFile: schema.files.contract_file || '',
        artifactFiles: schema.files.artefacts || [],
    };
};

// ============================================
// CONTRACT GUARANTEE HELPERS
// ============================================

export interface ContractGuaranteeState {
    items: ContractGuaranteeItem[];
    files: Map<string, File>;
    existingFiles: Map<string, string>;
}

/**
 * Extracts contract guarantee data from schema supporting multiple formats:
 * 1. collaterals.assets.contracts.items (new format)
 * 2. schema.contracts.items (cessão root format)
 * 3. schema.contract_guarantee (legacy single object)
 */
export const extractContractGuaranteeFromSchema = (schema: any): ContractGuaranteeState | null => {
    const items = resolveContractGuaranteeItems(schema);
    if (!items?.length) return null;

    const existingFiles = new Map<string, string>();
    items.forEach((item: any) => {
        if (item.file_name) {
            existingFiles.set(item.collateral_id || item.file_name, item.file_name);
        }
    });

    return {
        items: items.map((item: any) => ({
            collateral_id: item.collateral_id || crypto.randomUUID(),
            contract_number: item.contract_number || '',
            contract_value: item.contract_value || 0,
            anticipated_value: item.anticipated_value || 0,
            file_name: item.file_name || '',
            description: item.description || '',
        })),
        files: new Map(),
        existingFiles,
    };
};

const resolveContractGuaranteeItems = (schema: any): any[] | null => {
    if (schema.collaterals?.assets?.contracts?.items?.length) {
        return schema.collaterals.assets.contracts.items;
    }
    if (schema.contracts?.items?.length) {
        return schema.contracts.items;
    }
    if (schema.contract_guarantee) {
        return Array.isArray(schema.contract_guarantee)
            ? schema.contract_guarantee
            : [schema.contract_guarantee];
    }
    return null;
};
